import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  type ReactElement,
  type ReactNode,
} from 'react';
import { cn } from '../lib/cn';
import { Input } from './input';
import { Label } from './label';
import { Switch } from './switch';
import { Textarea } from './textarea';

export interface FormFieldProps {
  label?: ReactNode;
  description?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  htmlFor?: string;
  children: ReactNode;
  className?: string;
}

interface ChildProps {
  id?: string;
  'aria-describedby'?: string;
}

// Only these forward `id`/`aria-describedby` straight onto the DOM node the
// label needs to point at (native tags always do; `Input`/`Textarea`/`Switch`
// spread `...props` onto their root element — verified by reading each).
// Everything else is either a composite that swallows the prop (`Select` is
// `SelectPrimitive.Root`, a context provider with no DOM node of its own and
// no `id` in its prop type; `Combobox` destructures a fixed prop list with no
// `...rest`) or an unknown wrapper `<div>` — cloning onto either is either a
// silent no-op (acceptable: matches pre-existing behavior, caller still must
// wire it by hand) or, for a `<div>` that itself wraps an element which
// already carries its own `id`, an active regression: two DOM nodes sharing
// one id, with `label[for]` now resolving to the non-focusable wrapper
// instead of the real control. Restricting the clone to known-safe leaves
// avoids that regression while keeping the fix for the majority case this
// was written for.
const CLONEABLE_TYPES = new Set<unknown>(['input', 'textarea', 'select', Input, Textarea, Switch]);

export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  ({ label, description, error, required, htmlFor, children, className }, ref) => {
    const generatedId = useId();
    const fieldId = htmlFor ?? generatedId;
    const errorId = `${fieldId}-error`;
    const descriptionId = `${fieldId}-description`;
    const describedBy = error ? errorId : description ? descriptionId : undefined;

    // `<Label htmlFor={fieldId}>` above points at `fieldId`, but nothing
    // wired that id onto the actual control — a caller that forgot to set a
    // matching `id` on its own input shipped a label pointing at nothing,
    // invisible unless you query by label text or use a screen reader.
    // Clone the single child to fill in `id` — and `aria-describedby`, for
    // the same reason — only when the caller has not already set its own
    // value, so an explicit `id`/`aria-describedby` on the child always
    // wins. Multi-child, non-element, or non-cloneable-type children (see
    // `CLONEABLE_TYPES` above) fall through unchanged; those callers still
    // need their own `id` wired by hand.
    const singleChild = Children.count(children) === 1 && isValidElement(children) ? children : null;
    const child =
      singleChild && CLONEABLE_TYPES.has((singleChild as ReactElement).type)
        ? cloneElement(singleChild as ReactElement<ChildProps>, {
            id: (singleChild as ReactElement<ChildProps>).props.id ?? fieldId,
            ...(describedBy
              ? {
                  'aria-describedby':
                    (singleChild as ReactElement<ChildProps>).props['aria-describedby'] ?? describedBy,
                }
              : {}),
          })
        : children;

    return (
      <div ref={ref} className={cn('flex flex-col gap-1', className)}>
        {label ? (
          <Label htmlFor={fieldId} required={required}>
            {label}
          </Label>
        ) : null}
        {child}
        {error ? (
          <p id={errorId} role="alert" className="text-caption text-danger">
            {error}
          </p>
        ) : description ? (
          <p id={descriptionId} className="text-caption text-text-muted">
            {description}
          </p>
        ) : null}
      </div>
    );
  },
);
FormField.displayName = 'FormField';
